import React from 'react';
import { Row, Table } from 'reactstrap';
import BoldButton from '../../Common/BoldButton';
import useGetPurchaseOrderTransactions, { PurchaseOrderTransactionDto } from '../../Requests/PurchaseOrders/useGetPurchaseOrderTransactions';
import { PurchaseOrderDto } from '../../Requests/PurchaseOrders/useGetPurchaseOrders';
import QueryView from '../../Common/QueryView';

interface Props {
  purchaseOrder: PurchaseOrderDto,
  backClicked?: () => void
}

const PurchaseOrderTransactionPage = (props: Props) => {
  const getPoTransactions = useGetPurchaseOrderTransactions(props.purchaseOrder.id);

  const sortTransactions = (a: PurchaseOrderTransactionDto, b: PurchaseOrderTransactionDto) =>
    new Date(b.dateOfDelivery).getTime() - new Date(a.dateOfDelivery).getTime()

  return <>
    <h2 className="col-auto ka-blue">Purchase Order Transactions</h2>
    <Row className="justify-content-between mb-2">
      <BoldButton className="col-auto btn-ghost-secondary" variant="link" onClick={props.backClicked}>
        <i className="bi-chevron-left" /> Back
      </BoldButton>
    </Row>
    <QueryView
      query={getPoTransactions}
      renderData={transactions =>
        transactions.length === 0
          ? <h4>No Transactions found for Purchase Order {props.purchaseOrder.number}</h4>
          : <Row>
            <Table borderless>
              <tbody>
                {transactions
                  .sort((a, b) => sortTransactions(a, b))
                  .map(transaction =>
                    <TransactionDisplay key={transaction.id} transaction={transaction} />)
                }
              </tbody>
            </Table>
          </Row>
      } />
  </>
}

const TransactionDisplay = ({ transaction }: { transaction: PurchaseOrderTransactionDto }) => {
  return <>
    <tr>
      <th colSpan={2} className={"fw-bold"} style={{ borderBottom: "2px solid #677788" }}>
        <h3>Purchase Order Transaction {transaction.number}</h3>
      </th>
    </tr>
    <tr>
      <th className={"fw-bold"}>Transaction Details</th>
      <td>
        <Table className={"w-auto"} bordered>
          <thead>
            <tr>
              <th>Purchase Order Number</th>
              <th>Number</th>
              <th>Client Ticket</th>
              <th>Date of Delivery</th>
              <th>Site Name</th>
              <th>Purchase Order Ref</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{transaction.purchaseOrderNumber}</td>
              <td>{transaction.number}</td>
              <td>{transaction.clientReceivingTicketId}</td>
              <td>{new Date(transaction.dateOfDelivery).toLocaleString()}</td>
              <td>{transaction.site.name}</td>
              <td>{transaction.purchaseOrderId}</td>
            </tr>
          </tbody>
        </Table>
      </td>
    </tr>
    <tr>
      <th className={"fw-bold"}>Supplier</th>
      <td>
        <Table className={"w-auto"} bordered>
          <thead>
            <tr>
              <th>Name</th>
              <th>Number</th>
              <th>Supplier Ref</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{transaction.supplier.name}</td>
              <td>{transaction.supplier.number}</td>
              <td>{transaction.supplier.supplierId}</td>
            </tr>
          </tbody>
        </Table>
      </td>
    </tr>
    {
      transaction.driver &&
      <tr>
        <th className={"fw-bold"}>Driver</th>
        <td>
          <Table className={"w-auto"} bordered>
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Driver Ref</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{transaction.driver.name}</td>
                <td>{transaction.driver.number}</td>
                <td>{transaction.driver.driverId}</td>
              </tr>
            </tbody>
          </Table>
        </td>
      </tr>
    }
    {
      transaction.transport &&
      <tr>
        <th className={"fw-bold"}>Transport</th>
        <td>
          <Table className={"w-auto"} bordered>
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Transport Ref</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{transaction.transport.name}</td>
                <td>{transaction.transport.number}</td>
                <td>{transaction.transport.transportId}</td>
              </tr>
            </tbody>
          </Table>
        </td>
      </tr>
    }
    {
      transaction.carrier &&
      <tr>
        <th className={"fw-bold"}>Carrier</th>
        <td>
          <Table className={"w-auto"} bordered>
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
                <th>Carrier Ref</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{transaction.carrier.name}</td>
                <td>{transaction.carrier.number}</td>
                <td>{transaction.carrier.carrierId}</td>
              </tr>
            </tbody>
          </Table>
        </td>
      </tr>
    }
    <tr>
      <th>
        <div className={"fw-bold"}>Delivered</div>
      </th>
      <td>
        <Table bordered>
          <thead>
            <tr>
              <th>Product Name</th>
              <th>Product Ref</th>
              <th>Delivered</th>
              <th>Unit</th>
              <th>Density Value</th>
              <th>Density Mass Unit</th>
              <th>Density Volume Unit</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{transaction.productName}</td>
              <td>{transaction.productId}</td>
              <td>{transaction.delivered}</td>
              <td>{transaction.unit}</td>
              <td>{transaction.density?.value}</td>
              <td>{transaction.density?.massUnit}</td>
              <td>{transaction.density?.volumeUnit}</td>
            </tr>
          </tbody>
        </Table>
      </td>
    </tr>
  </>
}

export { PurchaseOrderTransactionPage };